import { useQuery } from '@tanstack/react-query';
import { getThreatActorProfile } from '../../../api/risk/threat-actor.api';
import type { ThreatActorMetadata, ThreatActorProfile } from '../types';

interface AxiosLikeError {
  response?: { status?: number };
}

function isNotFoundError(error: unknown): boolean {
  return (error as AxiosLikeError)?.response?.status === 404;
}

/**
 * Build a body-empty ThreatActorProfile from the citation metadata seed so
 * the panel header renders instantly while the rich payload is in flight.
 */
function seedProfile(seed: ThreatActorMetadata): ThreatActorProfile {
  return {
    mitreId: seed.mitreId,
    name: seed.name,
    aliases: seed.aliases ?? [],
    techniqueCount: seed.techniqueCount ?? 0,
    primaryMotivation: seed.primaryMotivation,
    resourceLevel: seed.resourceLevel,
    domains: seed.domains ?? [],
    firstSeen: seed.firstSeen,
    lastSeen: seed.lastSeen,
    associatedTechniques: [],
    arsenal: [],
    claimsImpact: null,
    references: [],
  };
}

export interface UseThreatActorProfileResult {
  profile: ThreatActorProfile | null;
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
  refetch: () => void;
}

export function useThreatActorProfile(
  mitreId: string | null,
  seed?: ThreatActorMetadata
): UseThreatActorProfileResult {
  const query = useQuery<ThreatActorProfile>({
    queryKey: ['threat-actor-profile', mitreId],
    queryFn: () =>
      getThreatActorProfile(mitreId as string).then((res) => res.data),
    enabled: !!mitreId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const seeded = seed ? seedProfile(seed) : null;
  const profile = query.data ?? seeded;

  return {
    profile,
    isLoading: query.isLoading && !!mitreId,
    isError: query.isError,
    isNotFound: query.isError && isNotFoundError(query.error),
    refetch: query.refetch,
  };
}
